'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api/client';
import Link from 'next/link';

interface TopbarProps {
  title: string;
  subtitle?: string;
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  city_manager: 'City Manager',
  support_agent: 'Support Agent',
};

/**
 * A notification, exactly as `GET /notifications` sends it.
 *
 * The previous shape asked for `message`, `notification_type`, `priority` and
 * `is_read`. None of those four columns exist -- the table has `body`, `kind`
 * and `read_at`, and no priority at all. The query was wrapped in `catch {}`,
 * so it failed on every page load in complete silence and the bell has never
 * shown a single notification since it was written.
 */
interface NotifItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

const NAV_LINKS = [
  { label: 'Dashboard', href: '/', icon: 'HomeIcon' },
  { label: 'Jobs', href: '/jobs', icon: 'TruckIcon' },
  { label: 'Document Review', href: '/document-review', icon: 'DocumentCheckIcon' },
  { label: 'Payouts', href: '/payouts', icon: 'BanknotesIcon' },
  { label: 'Markets', href: '/markets', icon: 'GlobeAltIcon' },
  { label: 'Users & Drivers', href: '/user-driver-management', icon: 'UsersIcon' },
  { label: 'Discounts & Pricing', href: '/pricing-surge-management', icon: 'TicketIcon' },
  { label: 'Settings', href: '/admin-settings', icon: 'Cog8ToothIcon' },
];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * A colour per kind of notification.
 *
 * This was keyed on `priority` -- critical, high, medium, low -- a column the
 * notifications table has never had. Every dot fell through to the default,
 * so the four colours were decoration over a field that was always undefined.
 * `kind` is real, and is what the API sends.
 */
const KIND_DOT: Record<string, string> = {
  dispute_raised: 'bg-red-500',
  dispute_escalated: 'bg-red-500',
  document_rejected: 'bg-orange-500',
  job_cancelled: 'bg-orange-500',
  payout_failed: 'bg-red-500',
  job_assigned: 'bg-amber-500',
  job_delivered: 'bg-emerald-500',
};

export default function Topbar({ title, subtitle }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [notifications, setNotifications] = useState<NotifItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const { profile } = useAuth();
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleDateString('en-CA', { day: '2-digit', month: 'short', year: 'numeric' }) +
          ', ' + now.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })
      );
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Through the API, like every other screen in this console.
   *
   * This used to open a Supabase client in the browser and query the table
   * directly, with an anon key, from a component rendered on every page --
   * around the API that decides who may read what. The API knows this is an
   * operator and returns their notifications; the browser has no business
   * holding a database connection.
   */
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get<{ unread: number; notifications: NotifItem[] }>(
        '/notifications',
      );
      setNotifications(res.notifications.slice(0, 10));
      setUnreadCount(res.unread);
    } catch {
      // Left as it was: a failed poll should not take the header down. It is
      // silent because there is nowhere sensible to put the error in a bell
      // icon -- but unlike before, the request it makes actually works.
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Polled rather than subscribed. The realtime channel this replaces was
    // a Supabase subscription on a table the console should not be reading
    // directly; a minute's delay on a notification badge is not worth a
    // second data path into the database.
    const poll = setInterval(fetchNotifications, 60000);
    return () => clearInterval(poll);
  }, [fetchNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut ⌘K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
        setNotifOpen(false);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNotifOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  const filteredLinks = searchQuery.trim()
    ? NAV_LINKS.filter((l) => l.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : NAV_LINKS;

  const markAllRead = async () => {
    if (!notifications.some((n) => !n.read_at)) return;
    setMarkingAll(true);
    try {
      await api.post('/notifications/read-all');
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
      setUnreadCount(0);
    } catch {
      // Not marked. Leaving the badge as it was is honest -- clearing it here
      // would tell an operator their queue was empty when it is not.
    }
    setMarkingAll(false);
  };

  const markOneRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // Same reasoning: the badge reflects the server, not the click.
    }
  };

  const displayRole = profile?.role ? (roleLabels[profile.role] || profile.role) : '';

  return (
    <header className="h-14 bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4 sticky top-0 z-30">
      {/* Title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-base font-600 text-foreground truncate">{title}</h1>
        {subtitle && (
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Search */}
        <div ref={searchRef} className="relative">
          <button
            onClick={() => { setSearchOpen(!searchOpen); setNotifOpen(false); }}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg px-3 py-1.5 transition-all duration-150 text-sm"
            aria-label="Search"
          >
            <Icon name="MagnifyingGlassIcon" size={15} variant="outline" />
            <span className="hidden sm:block text-xs text-muted-foreground">Search...</span>
            <kbd className="hidden sm:block text-[10px] bg-muted border border-border rounded px-1 py-0.5 font-mono text-muted-foreground">
              ⌘K
            </kbd>
          </button>

          {searchOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-modal z-50 overflow-hidden fade-in">
              <div className="p-3 border-b border-border">
                <div className="relative">
                  <Icon name="MagnifyingGlassIcon" size={14} variant="outline" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search pages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-background border border-border rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>
              <div className="py-1.5 max-h-64 overflow-y-auto">
                {filteredLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No pages found</p>
                ) : (
                  filteredLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Icon name={link.icon as any} size={14} variant="outline" className="text-muted-foreground" />
                      </div>
                      <span className="text-sm font-500 text-foreground">{link.label}</span>
                    </Link>
                  ))
                )}
              </div>
              <div className="px-3 py-2 border-t border-border bg-muted/20">
                <p className="text-[10px] text-muted-foreground">Press <kbd className="font-mono bg-muted border border-border rounded px-1">Esc</kbd> to close</p>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => { setNotifOpen(!notifOpen); setSearchOpen(false); }}
            className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all duration-150"
            aria-label="Notifications"
          >
            <Icon name="BellIcon" size={17} variant="outline" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full border-2 border-card" />
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-modal z-50 overflow-hidden fade-in">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-600 text-foreground">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="text-[10px] font-700 bg-primary text-white px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    disabled={markingAll}
                    className="text-xs text-primary hover:text-primary/80 font-500 transition-colors disabled:opacity-50"
                  >
                    {markingAll ? 'Marking...' : 'Mark all read'}
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-80 overflow-y-auto divide-y divide-border/50">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <Icon name="BellSlashIcon" size={24} variant="outline" className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">No notifications</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer ${!n.read_at ? 'bg-primary/3' : ''}`}
                      onClick={() => !n.read_at && markOneRead(n.id)}
                    >
                      {/* Coloured by what the notification is about. There is
                          no priority column -- the old dot read one that does
                          not exist, so every dot fell back to the same
                          colour. */}
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read_at ? (KIND_DOT[n.kind] || 'bg-primary') : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-600 text-foreground truncate ${!n.read_at ? 'font-700' : ''}`}>{n.title}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read_at && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-border bg-muted/20">
                <Link
                  href="/notification-feed"
                  onClick={() => setNotifOpen(false)}
                  className="text-xs text-primary hover:text-primary/80 font-500 transition-colors flex items-center gap-1"
                >
                  View all notifications
                  <Icon name="ArrowRightIcon" size={11} variant="outline" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-border" />

        {/* Role badge */}
        {displayRole && (
          <span className="hidden sm:block text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-500">
            {displayRole}
          </span>
        )}

        {/* Live indicator */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="hidden sm:block">Live</span>
        </div>

        {/* Timestamp */}
        {currentTime && (
          <span className="hidden md:block text-xs text-muted-foreground tabular-nums">
            {currentTime}
          </span>
        )}
      </div>
    </header>
  );
}